import React, { useRef, useState, useEffect } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import Link from 'redux-first-router-link';
import ProductSD from './ProductSD';
import Purchase from './Purchase';
import viewAnalytics from './viewAnalytics';
import Top from '../Top/Top';
import TemplateParser from '../Templates/TemplateParser';
import ProductPicker from '../Templates/Products/Products';
import ProductItem from '../Category/ProductItem';
import Slider from '../../hocs/Slider/Slider';
import Breadcrumbs from '../Category/Breadcrumbs';
import ErrorBoundary from '../../hocs/ErrorBoundary/ErrorBoundary';
import Pagination from '../../hocs/Pagination/Pagination';
import { addRecentlyViewed } from '../../actions/checkout';
import { resetFilter } from '../../actions/environment';
import { purifier, addEvent, removeEvent, height } from '../../utils/dom';
import { isImageLoaded } from '../../utils/image';
import { translate, prefix, translateObjectData } from '../../utils/locale';
import { urlify } from '../../utils/string';
import { origin } from '../../config';
import s from './Product.scss';

const Product = ({ data, currentPath, setup, productCache, filter, recentlyViewed, listItemCache, ...props }) => {
	if (!data || !data.productData || !productCache[setup.market] || !productCache[setup.market][setup.pricelist]) return null;
	const product = translateObjectData(setup, data.productData);
	const soldOut = product.markets[setup.market].stockOfAllItems === 0;
	const price = product.markets[setup.market].pricesByPricelist[setup.pricelist];
	const discount = price.discountPercent || (100 - Math.round((price.priceAsNumber / price.priceBeforeDiscountAsNumber) * 100));
	const descriptionParts = product.description.split(/<br\s?\/>/);
	const sizes = Object.keys(product.items).map((item) => ({ name: product.items[item].name, item, stock: product.items[item].stockByMarket[setup.market] }));
	const shareLocation = `${origin}${prefix(setup, product.canonicalUri)}`;
	const productSections = data.parts || [];
	const productRelations = { variant: [] };

	if (product?.relatedProducts) {
		product.relatedProducts.forEach((rP) => {
			if (!productRelations[rP.relation]) productRelations[rP.relation] = [];
			productRelations[rP.relation] = [...productRelations[rP.relation], rP.product];
		});
	}

	const images = product.media.map((media) => media.sources.zoom.url);

	const metaInfo = {
		title: `${product.brandName} — ${product.name} ${product.variantName}`,
		metaDescription: product.metaDescription,
		linkCanonical: shareLocation,
		images,
	};

	const badges = [];
	let newSet = false;
	let saleSet = false;

	if (product.badges) {
		Object.keys(product.badges).forEach((badge) => {
			if (!!product.badges[badge].isNew && !price.newProduct) return;
			if (!!product.badges[badge].isNew && price.newProduct) newSet = true;
			if (!!product.badges[badge].isSale && !price.showAsOnSale) return;
			if (!!product.badges[badge].isSale && price.showAsOnSale) saleSet = true;
			if (!!product.badges[badge].isSoldOut && !soldOut) return;
			if (product.badges[badge].image) {
				badges.push(<span key={badge} className={[s.productDetail_badge, s.productDetail_badge__svg].join(' ')}><img src={product.badges[badge].image.url} alt="" /></span>);
			}
		});
	}
	if (!soldOut && price.newProduct && !newSet) {
		badges.unshift(<span key="new-badge" className={[s.productDetail_badge, s.productDetail_badge__new].join(' ')}>{ translate(setup, 'new') }</span>);
	} else if (!soldOut && price.showAsOnSale && !saleSet) {
		badges.unshift(<span key="sale-badge" className={[s.productDetail_badge, s.productDetail_badge__sale].join(' ')}>-{ discount }%</span>);
	}

	// Expand/collapse product description.
	const [wholeDescription, showWholeDescription] = useState();
	const wholeDescriptionHandler = (e) => {
		e.preventDefault();
		showWholeDescription((prevState) => !prevState);
	};

	// Expand/collapse product attributes.
	const [attribute, setAttribute] = useState();
	const toggleAttributes = (e) => {
		e.preventDefault();
		const index = [...e.currentTarget.parentNode.parentNode.children].indexOf(e.currentTarget.parentNode);

		setAttribute((prevState) => (prevState === index ? null : index));
	};

	// Determine height of sticky section to set top offset.
	const summarySection = useRef();
	const [windowHeight, updateWindowHeight] = useState();
	useEffect(() => {
		const resizeHandler = () => {
			updateWindowHeight(window.innerHeight);
		};

		addEvent(window, 'resize.selectionFixed', () => {
			window.requestAnimationFrame(resizeHandler);
		});

		resizeHandler();

		return () => {
			removeEvent(window, 'resize.selectionFixed');
		};
	});

	const [summarySectionHeight, updateSummarySectionHeight] = useState();
	useEffect(() => {
		let getRefTimeoutHandler;
		const getRef = () => {
			// Is main container visible.
			if (!summarySection.current) {
				getRefTimeoutHandler = setTimeout(getRef, 200);
				return;
			}
			updateSummarySectionHeight(height(summarySection.current));

			// Are all images loaded.
			const $imgs = summarySection.current.querySelectorAll('img');
			let allLoaded = true;
			[...$imgs].forEach(($img) => {
				if (!isImageLoaded($img)) allLoaded = false;
			});
			if (!allLoaded) {
				getRefTimeoutHandler = setTimeout(getRef, 200);
				return;
			}

			clearTimeout(getRefTimeoutHandler);
			updateSummarySectionHeight(height(summarySection.current));
		};

		getRef();
	}, [product, summarySection, currentPath, wholeDescription, attribute]);

	const summarySectionPosition = windowHeight > summarySectionHeight + 100 ? '70px' : `calc(100vh - ${summarySectionHeight + 25}px)`;

	const [analytics, analyticsShown] = useState(false);
	useEffect(() => {
		if (typeof window.dataLayer === 'undefined') window.dataLayer = [];
		if (analytics !== currentPath) {
			props.addRecentlyViewed(product.product);
			viewAnalytics(price, product, setup, listItemCache);
			analyticsShown(currentPath);
		}
	}, [currentPath, product, price, setup, analytics]);

	const variants = [...productRelations.variant.map((item) => {
		const cachedItem = productCache[setup.market][setup.pricelist].products[item];
		return { ...cachedItem.swatch, uri: cachedItem.uri };
	}), { ...product.swatch, selected: true }].sort((a, b) => (parseInt(a.order, 10) > parseInt(b.order, 10) ? 1 : -1));

	return (
		<>
			<main role="main">
				<Top setup={setup} pageContent={metaInfo} paths={data.paths} skipGeneral />
				<ProductSD metaInfo={metaInfo} product={product} soldOut={soldOut} price={price} currency={setup.countries[setup.country].currency} image={images} />
				<div id="productTop" className={s.product}>
					<Breadcrumbs currentPath={currentPath} filter={filter} filtering={productCache[setup.market][setup.pricelist]} data={data} setup={setup} product={{ uri: prefix(setup, product.canonicalUri), name: product.name }} resetFilter={props.resetFilter} />
					<ErrorBoundary message="Product images missing data" debugData={product}>
						<div className={s.productImages}>
							{
								product.media.length > 0 && (
									<Slider screenMaxWidthDisable={600} infinite>
										{
											product.media.map((media, i) => {
												const extraAttributes = {};
												if (product.film && i === 0) extraAttributes['data-film'] = product.film;
												const mediaSizes = media?.sources?.medium || media?.sources?.standard;
												if (!mediaSizes) return null;
												return (
													<div key={mediaSizes.url.slice(mediaSizes.url.lastIndexOf('/') + 1)} className={[s.productImages_media, (product.film && i === 0 ? s.productImages_media__film : null)].join(' ')} {...extraAttributes}>
														<img className={s.productImages_media_image} src={mediaSizes.url} alt={`${product.brandName} ${product.name}`} />
													</div>
												);
											})
										}
									</Slider>
								)
							}
						</div>
					</ErrorBoundary>
					<div className={s.productDetail} ref={summarySection} style={{ top: typeof window !== 'undefined' && window.innerWidth > 600 ? summarySectionPosition : 0 }}>

						<ErrorBoundary message="Product details missing data" debugData={product}>
							<div className={s.productDetail_badges}>
								{ badges }
							</div>
							<div className={s.productDetail_top}>
								<h1 className={s.productDetail_top_title}>
									{product.name}
									<span className={s.productDetail_top_title_variant}>{product.brandName} — {product.variantName}</span>
								</h1>
							</div>

							{
								variants && variants.length > 1 && (
									<ErrorBoundary message="Related Product details missing data" debugData={productRelations}>
										<div className={s.productDetail_variants}>
											{
												variants.map((item) => {
													const VariantTag = item.uri ? Link : 'span';
													const attrs = {};
													const key = item.uri || `${item.desc}-${item.hex}`;
													if (item.uri) {
														attrs.to = prefix(setup, `${currentPath.slice(0, currentPath.lastIndexOf('/'))}/${item.uri}`);
													}
													return (
														<VariantTag key={key} className={[s.productDetail_variant, (item.selected ? s.productDetail_variant__selected : null)].join(' ')} {...attrs}>
															{
																item.image ? (
																	<span className={s.productDetail_variant_swatch} style={{ backgroundImage: `url(${item.image})` }} />
																) : (
																	<span className={s.productDetail_variant_swatch} style={{ backgroundColor: item.hex || '#000' }} />
																)
															}
														</VariantTag>
													);
												})
											}
										</div>
									</ErrorBoundary>
								)
							}

							<div className={[s.productDetail_info, (wholeDescription ? [s.productDetail_info__all] : null)].join(' ')}>
								<p className={s.productDetail_info_top}>{ descriptionParts[0] }</p>
								<div className={s.productDetail_info_bottom} dangerouslySetInnerHTML={{ __html: purifier(descriptionParts.slice(1).join('<br />')) }} />
								{
									descriptionParts.length > 1 && !wholeDescription && (
										<a onClick={wholeDescriptionHandler} className={s.productDetail_info_all} href="#read-more">{ translate(setup, 'productInfoExpand') }</a>
									)
								}
								{
									descriptionParts.length > 1 && wholeDescription && (
										<a onClick={wholeDescriptionHandler} className={s.productDetail_info_all} href="#hide-read-more">{ translate(setup, 'productInfoCollapse') }</a>
									)
								}
							</div>

							<div className={s.productDetail_purchase}>
								<Purchase setup={setup} currentPath={currentPath} productId={data.productData.product} originalProduct={data.productData} extraProducts={productRelations.bundle} sizes={sizes} price={price} soldOut={soldOut ? translate(setup, 'soldOut') : false} />
							</div>
						</ErrorBoundary>
						<ErrorBoundary message="Product attributes missing data" debugData={product.product_attributes}>
							<div className={s.productDetail_attributes}>
								{
									product.product_attributes && Object.keys(product.product_attributes).map((attr, i) => (
										<div key={attr} className={[s.productDetail_attribute, (attribute === i ? s.productDetail_attribute__open : null)].join(' ')}>
											<a href="#open-attribute" onClick={toggleAttributes} className={s.productDetail_attribute_heading}>
												{product.product_attributes[attr].heading}
												<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
													<g transform="translate(1 1)"><line x1="7.5" x2="7.5" y2="14" /><line x1="14" y1="7" y2="7" /></g>
												</svg>
											</a>
											<div className={s.productDetail_attribute_inner}>
												<p className={s.productDetail_attribute_inner_text}>{product.product_attributes[attr].desc}</p>
											</div>
										</div>
									))
								}
								{
									Object.keys(product.measurementChartRows).length > 0 && (
										<div className={[s.productDetail_attribute, (attribute === Object.keys(product.product_attributes || {}).length ? s.productDetail_attribute__open : null)].join(' ')}>
											<a href="#open-attribute" onClick={toggleAttributes} className={s.productDetail_attribute_heading}>
												{ translate(setup, 'sizeGuide') }
												<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
													<g transform="translate(1 1)"><line x1="7.5" x2="7.5" y2="14" /><line x1="14" y1="7" y2="7" /></g>
												</svg>
											</a>
											<div className={s.productDetail_attribute_inner}>
												<table className={s.productDetail_attribute_inner_table}>
													<thead>
														<tr>
															<th />
															{
																Object.keys(product.measurementChartRows).slice(0, 1).map((row) => (
																	Object.keys(product.measurementChartRows[row]).map((key) => <th key={key}>{ key.replace(' / ', '\n') }</th>)
																))
															}
														</tr>
													</thead>
													<tbody>
														{
															Object.keys(product.measurementChartRows).map((row) => (
																<tr key={urlify(`${row}`)}>
																	<td>{ row }</td>
																	{
																		Object.keys(product.measurementChartRows[row]).map((key) => <td key={urlify(`${row}-${key}`)}>{ product.measurementChartRows[row][key].replace(' / ', '\n') }</td>)
																	}
																</tr>
															))
														}
													</tbody>
												</table>
											</div>
										</div>
									)
								}
							</div>
						</ErrorBoundary>
					</div>
				</div>
				{
					productRelations?.standard && (
						<ErrorBoundary message="Related Product details missing data" debugData={productRelations}>
							<div className={s.productRelations}>
								<h3 className={s.productRelations_title}>{ translate(setup, 'relatedProducts') }</h3>
								<div className={s.productRelations_inner}>
									<Pagination ListComponent={ProductItem} list={productRelations?.standard.filter((pR) => pR !== product.product)} cache={productCache[setup.market][setup.pricelist].products} currentPath="" canonical />
								</div>
							</div>
						</ErrorBoundary>
					)
				}
				{
					productSections.length > 0 && (
						<div className={s.productCMS}>
							{
								productSections.map((section) => (
									<TemplateParser sections={section.sections} setup={setup} isProduct={product.product} />
								))
							}
						</div>
					)
				}
				{
					recentlyViewed && recentlyViewed.length > 0 && (
						<ErrorBoundary message="Recently Viewed Product details missing data" debugData={recentlyViewed}>
							<div className={s.productRelations}>
								<h3 className={s.productRelations_title}>{ translate(setup, 'recentlyViewedProducts') }</h3>
							</div>
							<ProductPicker setup={setup} content={{ individual: recentlyViewed, limit: 8, variation: ['m_scroll'], identifier: 'recentlyViewed' }} isProduct={product.product} />
						</ErrorBoundary>
					)
				}
			</main>
		</>
	);
};

const mapState = ({ environment, productCache, checkout, filter }, { data, currentPath }) => ({
	data,
	currentPath,
	header: environment.header,
	setup: environment.setup,
	productCache,
	recentlyViewed: checkout.recentlyViewed,
	listItemCache: checkout.listItemCache,
	filter,
});

export default hot(module)(connect(mapState, { resetFilter, addRecentlyViewed })(Product));
